import { useRef, useState, useEffect, memo } from "react";
import { http, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Camera, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

// Foto profil disajikan lewat endpoint PUBLIK /api/avatars/<path> (tanpa token).
// Path memuat uuid unik per unggahan, jadi URL stabil & berbeda tiap ganti foto —
// browser cache "immutable". Karena tidak butuh token, URL tidak pernah 401 saat
// access token kedaluwarsa/berganti, sehingga foto tidak hilang-muncul lagi.
export const avatarUrl = (path) =>
  path ? `${API}/avatars/${path}` : null;

// Deterministic, on-brand fallback when a user has no photo: up to 2 initials on
// a colour derived from the name (same name → same colour everywhere).
const AVATAR_COLORS = [
  "#0f766e", "#1d4ed8", "#b45309", "#be123c", "#047857", "#7c2d12",
  "#0369a1", "#9d174d", "#4d7c0f", "#a16207", "#5b21b6", "#065f46",
];
export const avatarColor = (name = "") => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
export const avatarInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const UserAvatar = memo(function UserAvatar({ user, size = 32, className = "", onClick, testid = "header-user-avatar" }) {
  const path = user?.avatar_path || null;
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [path]);
  // Stable, immutable-cached URL (no ?v= cache-buster): once the browser has the
  // photo it keeps showing it across re-renders — no more hilang-muncul flicker.
  const src = path && !broken ? avatarUrl(path) : null;
  const name = user?.name || "";
  return (
    <button type="button" onClick={onClick} data-testid={testid} title={onClick ? "Ganti foto profil" : name}
      className={`relative rounded-full overflow-hidden text-white flex items-center justify-center font-semibold shrink-0 group ${onClick ? "cursor-pointer" : ""} ${className}`}
      style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.4)), background: src ? "#0f172a" : avatarColor(name) }}>
      {src ? (
        <img src={src} alt={name || "avatar"} className="w-full h-full object-cover" loading="lazy" decoding="async" onError={() => setBroken(true)} />
      ) : <span className="select-none leading-none" data-testid={`${testid}-initials`}>{avatarInitials(name)}</span>}
      {onClick && <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"><Camera size={12} /></span>}
    </button>
  );
});

export default function AvatarDialog({ open, onOpenChange }) {
  const { user, refreshUser } = useAuth();
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [broken, setBroken] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setBroken(false); }, [user?.avatar_path]);

  const pick = (f) => {
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) return toast.error("Gunakan JPG, PNG, atau WEBP");
    if (f.size > 3 * 1024 * 1024) return toast.error("Maksimal 3 MB");
    setFile(f); setPreview(URL.createObjectURL(f));
  };
  const save = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await http.post("/auth/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto profil diperbarui"); await refreshUser?.(); onOpenChange(false); setFile(null); setPreview(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengunggah foto"); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await http.delete("/auth/me/avatar"); toast.success("Foto profil dihapus"); await refreshUser?.(); onOpenChange(false); }
    catch { toast.error("Gagal menghapus"); } finally { setBusy(false); }
  };
  const current = preview || (user?.avatar_path && !broken ? avatarUrl(user.avatar_path) : null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="dlg-avatar">
        <DialogHeader>
          <DialogTitle>Foto Profil</DialogTitle>
          <DialogDescription>Gunakan foto wajah Anda sendiri agar rekan kerja dan audit mudah mengenali. Hanya Anda yang bisa mengubahnya.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3">
          <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow flex items-center justify-center text-4xl font-bold text-white" data-testid="avatar-preview"
            style={{ background: current ? "#f1f5f9" : avatarColor(user?.name || "") }}>
            {current ? <img src={current} alt="preview" className="w-full h-full object-cover" onError={() => setBroken(true)} /> : <span className="select-none">{avatarInitials(user?.name || "")}</span>}
          </div>
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" capture="user" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="avatar-file-input" />
          <Button variant="outline" onClick={() => inputRef.current?.click()} data-testid="avatar-pick-btn"><Upload size={14} className="mr-2" /> Pilih / Ambil Foto</Button>
          <div className="text-[11px] text-slate-500 text-center">JPG/PNG/WEBP, maks 3 MB. Di HP bisa langsung pakai kamera depan.</div>
        </div>
        <DialogFooter className="flex-row justify-between sm:justify-between">
          {user?.avatar_path ? <Button variant="ghost" className="text-rose-600" onClick={remove} disabled={busy} data-testid="avatar-remove-btn"><Trash2 size={14} className="mr-1" /> Hapus</Button> : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
            <Button onClick={save} disabled={!file || busy} data-testid="avatar-save-btn">{busy ? "Mengunggah..." : "Simpan"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
